import moment from 'jalali-moment'
import { DigitToMonth } from 'utilities/helper/digitToMonth'

// `jalali_time` arrives as «1405-06-26 15:40:04.000»: a Jalali date and a wall
// clock, both already in Tehran time.
const JALALI_TIME_FORMAT = 'jYYYY-jMM-jDD HH:mm:ss.SSS'

// Iran dropped daylight saving in 1401, so Tehran is a fixed +03:30. «امروز»
// has to be today THERE — the rows are stamped in Tehran time whatever zone the
// phone is set to.
const TEHRAN_UTC_OFFSET_MINUTES = 210

// Indexed by moment's `.day()`, which counts from Sunday.
const WEEKDAYS = [
    'یکشنبه',
    'دوشنبه',
    'سه‌شنبه',
    'چهارشنبه',
    'پنجشنبه',
    'جمعه',
    'شنبه',
]

export interface ITransactionDay {
    /** «1405-06-26», or '' for rows whose date could not be read */
    key: string
    /** «امروز», «دیروز», or the weekday */
    title: string
    /** «۲۶ شهریور ۱۴۰۵» */
    date: string
    rows: IMoneyPoolTransactionResult[]
}

export function dayKey(jalaliTime: string | null | undefined): string {
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(jalaliTime ?? '')
    return match ? match[1] : ''
}

function tehranDayKey(now: Date, daysAgo: number): string {
    return moment(now)
        .utcOffset(TEHRAN_UTC_OFFSET_MINUTES)
        .subtract(daysAgo, 'days')
        .format('jYYYY-jMM-jDD')
}

export function describeDay(
    key: string,
    now: Date = new Date()
): { title: string; date: string } {
    const parsed = moment(key, 'jYYYY-jMM-jDD')
    if (!key || !parsed.isValid()) return { title: 'تاریخ نامشخص', date: '' }

    const date = `${parsed.jDate()} ${
        DigitToMonth[parsed.jMonth() + 1]
    } ${parsed.jYear()}`

    if (key === tehranDayKey(now, 0)) return { title: 'امروز', date }
    if (key === tehranDayKey(now, 1)) return { title: 'دیروز', date }
    return { title: WEEKDAYS[parsed.day()], date }
}

/**
 * «۱۵:۴۰» — or nothing for a transaction recorded against a date alone, which
 * the server stamps at midnight. Showing «۰۰:۰۰» would claim a time nobody
 * entered. The details sheet asks for the seconds too; a row does not.
 */
export function timeOfDay(
    jalaliTime: string | null | undefined,
    withSeconds = false
): string {
    const parsed = moment(jalaliTime ?? '', JALALI_TIME_FORMAT)
    if (!parsed.isValid()) return ''
    if (parsed.format('HH:mm:ss') === '00:00:00') return ''
    return parsed.format(withSeconds ? 'HH:mm:ss' : 'HH:mm')
}

/**
 * Split the list into runs of the same day, in the order it came.
 *
 * Runs, not buckets: the server decides the order, and a search or a future
 * sort must not be silently re-sorted here into something it did not return.
 */
export function groupByDay(
    rows: IMoneyPoolTransactionResult[],
    now: Date = new Date()
): ITransactionDay[] {
    const days: ITransactionDay[] = []
    rows.forEach((row) => {
        const key = dayKey(row.jalali_time)
        const last = days[days.length - 1]
        if (last && last.key === key) {
            last.rows.push(row)
            return
        }
        days.push({ key, ...describeDay(key, now), rows: [row] })
    })
    return days
}
